"use client";

import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X, RotateCw } from "lucide-react";
import { cn } from "@shared/src/utils/cn";

type ToastPosition = "top-center" | "top-left" | "top-right" | "bottom-left" | "bottom-right" | "bottom-center";

// Create context for managing viewports
type ViewportContextType = {
  viewports: Map<ToastPosition, HTMLElement>;
  registerViewport: (position: ToastPosition, element: HTMLElement) => void;
  unregisterViewport: (position: ToastPosition) => void;
};

const ToastViewportContext = React.createContext<ViewportContextType | null>(null);

// Custom Provider that wraps Radix's provider
const ToastProvider = ({
  children,
  swipeDirection = "right",
}: {
  children: React.ReactNode;
  swipeDirection?: "up" | "down" | "left" | "right";
}) => {
  const [viewports, setViewports] = React.useState<Map<ToastPosition, HTMLElement>>(new Map());

  const registerViewport = React.useCallback((position: ToastPosition, element: HTMLElement) => {
    setViewports(prev => {
      const next = new Map(prev);
      next.set(position, element);
      return next;
    });
  }, []);

  const unregisterViewport = React.useCallback((position: ToastPosition) => {
    setViewports(prev => {
      const next = new Map(prev);
      next.delete(position);
      return next;
    });
  }, []);

  return (
    <ToastViewportContext.Provider value={{ viewports, registerViewport, unregisterViewport }}>
      <ToastPrimitives.Provider duration={5000} swipeDirection={swipeDirection}>
        {children}
      </ToastPrimitives.Provider>
    </ToastViewportContext.Provider>
  );
};

const useToastViewportContext = () => {
  const context = React.useContext(ToastViewportContext);
  if (!context) {
    throw new Error("useToastViewportContext must be used within a ToastProvider");
  }
  return context;
};

const toastPositionVariants = cva(
  "fixed z-[9999] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:max-w-[420px]",
  {
    variants: {
      position: {
        "top-center": "top-0 left-1/2 -translate-x-1/2",
        "top-left": "top-0 left-0",
        "top-right": "top-0 right-0",
        "bottom-left": "bottom-0 left-0",
        "bottom-right": "bottom-0 right-0",
        "bottom-center": "bottom-0 left-1/2 -translate-x-1/2",
      },
    },
    defaultVariants: {
      position: "top-center",
    },
  },
);

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport> & {
    position: ToastPosition;
  }
>(({ className, position, ...props }, ref) => {
  const { registerViewport, unregisterViewport } = useToastViewportContext();

  React.useEffect(() => {
    const element = (ref as React.RefObject<HTMLElement>)?.current;
    if (element) {
      registerViewport(position, element);
      return () => unregisterViewport(position);
    }
  }, [position, ref, registerViewport, unregisterViewport]);

  return (
    <ToastPrimitives.Viewport ref={ref} className={cn(toastPositionVariants({ position }), className)} {...props} />
  );
});
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-2xl border p-4 shadow-lg transition-all",
  {
    variants: {
      variant: {
        default: "border-border-secondary bg-surfaces-bg-secondary",
        destructive: "border-border-error bg-surfaces-bg-error text-text-error",
        success: "border-border-success bg-surfaces-bg-success text-text-success",
      },
      position: {
        "top-center": [
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out",
          "data-[state=closed]:fade-out-80",
          "data-[state=open]:slide-in-from-top-full data-[state=closed]:slide-out-to-top-full",
        ],
        "top-left": [
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out",
          "data-[state=closed]:fade-out-80",
          "data-[state=open]:slide-in-from-left-full data-[state=closed]:slide-out-to-left-full",
        ],
        "top-right": [
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out",
          "data-[state=closed]:fade-out-80",
          "data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full",
        ],
        "bottom-left": [
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out",
          "data-[state=closed]:fade-out-80",
          "data-[state=open]:slide-in-from-left-full data-[state=closed]:slide-out-to-left-full",
        ],
        "bottom-right": [
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out",
          "data-[state=closed]:fade-out-80",
          "data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full",
        ],
        "bottom-center": [
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out",
          "data-[state=closed]:fade-out-80",
          "data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full",
        ],
      },
    },
    defaultVariants: {
      variant: "default",
      position: "top-center",
    },
  },
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants> & {
      onReload?: () => void;
      permanent?: boolean;
      dismissible?: boolean;
    }
>(({ className, variant, position, onReload, permanent = false, dismissible = true, children, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant, position }), className)}
      duration={permanent ? Infinity : 5000}
      {...props}
    >
      {children}
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-error/30 group-[.destructive]:hover:border-error/30 group-[.destructive]:hover:bg-error group-[.destructive]:hover:text-error-foreground group-[.destructive]:focus:ring-error",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-1 top-1 rounded-md p-1 text-text-secondary hover:text-text-primary focus:opacity-100 focus:outline-none focus:ring-1 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title ref={ref} className={cn("text-sm font-semibold text-text-primary", className)} {...props} />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm text-text-secondary overflow-y-auto custom-scrollbar max-h-[80px]", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

const ToastReload = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, onClick, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-destructive text-destructive-foreground hover:bg-destructive/90 h-8 px-3",
        className,
      )}
      onClick={onClick}
      {...props}
    >
      <RotateCw className="h-4 w-4 mr-2" />
    </button>
  ),
);
ToastReload.displayName = "ToastReload";

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;
type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  ToastReload,
  // Export the position type and context
  type ToastPosition,
  useToastViewportContext,
};
